// Third-party
import React from 'react';
import DOMPurify from 'dompurify';

// Components
import DatesDisclaimer from './DatesDisclaimer';
import DetailedDatesDisclaimer from './DetailedDatesDisclaimer';

// Utils
import { formatLocalizedString, formatMessageWithLocale } from '@/utils/i18n/i18n';
import {
  getHoldingsAsAtDates,
  getDetailedHoldingsAsAtDates,
  getRollingDisclaimerDates,
  getRiskDataAsAtDates,
  getPerformanceAsAtDates,
} from '@/utils/disclaimers/comparison';

// Types
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import RollingIntervals from '@/types/rolling-intervals';
import LocalizedString from '@/types/localized-string';

interface Props {
  config: {
    customString?: LocalizedString;
    datesItemPrefix?: string;
    interval?: RollingIntervals;
    isHTML?: boolean;
    useDates?: boolean;
    useDetailedDates?: boolean;
    usePerformanceDates?: boolean;
    useRiskDates?: boolean;
    useRollingDates?: boolean;
  };
  items: ComparisonItem[];
  locale?: Locale;
  showItemNames?: boolean;
}

const CustomDisclaimer = ({ config, items, locale, showItemNames }: Props) => {
  const defaultDatesDisclaimer = () => (
    <DatesDisclaimer
      globalPrefix={formatLocalizedString(config.customString, locale)}
      itemPrefix={formatMessageWithLocale('Disclaimers.AsAt', locale)}
      itemDates={getHoldingsAsAtDates(items, locale)}
      showItemNames={showItemNames}
      locale={locale}
    />
  );

  if (config.useDates) {
    return defaultDatesDisclaimer();
  }

  if (config.useDetailedDates) {
    return (
      <DetailedDatesDisclaimer
        globalPrefix={formatLocalizedString(config.customString, locale)}
        itemPrefix={formatMessageWithLocale(`Disclaimers.${config.datesItemPrefix ? config.datesItemPrefix : 'AsAt'}`, locale)}
        itemDates={getDetailedHoldingsAsAtDates(items, locale)}
        showItemNames={showItemNames}
        locale={locale}
      />
    );
  }

  if (config.useRiskDates) {
    return (
      <DatesDisclaimer
        showItemNames={showItemNames}
        globalPrefix={formatLocalizedString(config.customString, locale)}
        itemPrefix={formatMessageWithLocale('Disclaimers.EndingOn', locale)}
        itemDates={getRiskDataAsAtDates(items, locale)}
        locale={locale}
      />
    );
  }

  if (config.usePerformanceDates) {
    return (
      <DatesDisclaimer
        globalPrefix={formatLocalizedString(config.customString, locale)}
        itemDates={getPerformanceAsAtDates(items, locale)}
        itemPrefix={formatMessageWithLocale('Disclaimers.EndingOn', locale)}
        locale={locale}
        showItemNames={showItemNames}
      />
    );
  }

  if (config.useRollingDates) {
    if (!config.interval) {
      return defaultDatesDisclaimer();
    }

    const itemsWithHoldings = items.filter(item => item.holdings.length > 0);
    return (
      <DatesDisclaimer
        globalPrefix={formatLocalizedString(config.customString, locale)}
        itemPrefix={formatMessageWithLocale(`Disclaimers.${config.datesItemPrefix ? config.datesItemPrefix : 'AsAt'}`, locale)}
        itemDates={getRollingDisclaimerDates(itemsWithHoldings, config.interval, undefined, locale)}
        showItemNames={showItemNames}
        locale={locale}
      />
    );
  }

  if (config.customString !== undefined) {
    const str: string = formatLocalizedString(config.customString, locale);
    if (config.isHTML) {
      return (
        <span className="disclaimer" data-private data-dd-privacy="mask">
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(str) }} />
        </span>
      );
    }

    return (
      <span className="disclaimer" data-private data-dd-privacy="mask">
        <span>{str}</span>
      </span>
    );
  }

  return null;
};

export default CustomDisclaimer;
